import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/presentation/hooks/useAuth";
import { CompanyListPage } from "./CompanyListPage";
import { UserListPage } from "./UserListPage";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Tabs } from "../../../../ui/components/Tabs";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherLogOut,
  FeatherUsers,
  FeatherBuilding,
  FeatherBarChart3,
  FeatherFileText,
} from "@subframe/core";
import { ComplianceCalendarPage } from "../../../compliance/presentation/pages/master/ComplianceCalendarPage";

interface AdminDashboardProps {
  onLogout: () => void;
}

type AdminSection = "overview" | "companies" | "users" | "compliance";

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getInitialSection = (): AdminSection => {
    const tab = searchParams.get("tab");
    if (tab && ["overview", "companies", "users"].includes(tab)) {
      return tab as AdminSection;
    }
    return "overview";
  };

  const [activeSection, setActiveSection] = useState<AdminSection>(
    getInitialSection()
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["overview", "companies", "users", "compliance"].includes(tab)) {
      const newSection = tab as AdminSection;
      setActiveSection((prevSection) => {
        return newSection !== prevSection ? newSection : prevSection;
      });
    }
  }, [searchParams]);

  const handleTabClick = (section: AdminSection): void => {
    setActiveSection(section);
    const currentPath = window.location.pathname;
    navigate(`${currentPath}?tab=${section}`, { replace: true });
  };

  const renderSection = (): React.ReactNode => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="flex w-full flex-col items-start gap-6 p-8">
            {/* Welcome Card */}
            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Welcome to Master Admin Dashboard
                </span>
                <span className="text-body font-body text-subtext-color">
                  Manage all companies and users from this central location.
                </span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex w-full flex-wrap items-start gap-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
                      <FeatherBuilding className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        Companies
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        Manage tenant companies
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="brand-primary"
                    onClick={() => handleTabClick("companies")}
                  >
                    View Companies
                  </Button>
                </div>
              </div>

              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
                      <FeatherUsers className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        Users
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        Manage all users
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="brand-primary"
                    onClick={() => handleTabClick("users")}
                  >
                    View Users
                  </Button>
                </div>
              </div>

              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
                      <FeatherFileText className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        Compliance
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        Manage compliance types and calendar
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="brand-primary"
                    onClick={() => handleTabClick("compliance")}
                  >
                    View Compliance
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case "companies":
        return <CompanyListPage />;

      case "users":
        return <UserListPage />;

      case "compliance":
        return <ComplianceCalendarPage />;

      default:
        return null;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar size="large" variant="brand">
                {user?.userName?.[0]?.toUpperCase() || user?.email[0]?.toUpperCase() || "A"}
              </Avatar>
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Master Admin Dashboard
                  </span>
                  <Badge variant="brand">Master Admin</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Welcome back, {user?.email}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherLogOut />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </div>

          {/* Navigation Tabs */}
          <Tabs>
            <Tabs.Item
              active={activeSection === "overview"}
              onClick={() => handleTabClick("overview")}
            >
              <FeatherBarChart3 className="h-4 w-4" />
              Overview
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "companies"}
              onClick={() => handleTabClick("companies")}
            >
              <FeatherBuilding className="h-4 w-4" />
              Companies
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "users"}
              onClick={() => handleTabClick("users")}
            >
              <FeatherUsers className="h-4 w-4" />
              Users
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "compliance"}
              onClick={() => handleTabClick("compliance")}
            >
              <FeatherFileText className="h-4 w-4" />
              Compliance
            </Tabs.Item>
          </Tabs>
        </div>

        {/* Dynamic Section Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          {renderSection()}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default AdminDashboard;

